import React, { useCallback, useRef, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import { Stack } from 'react-bootstrap'
import { useFormikContext } from 'formik'
import type { FormikProps } from 'formik'
import type { ReactNode } from 'react'
import type { PivotLayout, PivotLayoutField, PivotLayoutFilter } from '@ztechapps/common.contract'
import type { PivotData, PivotFilterSetting, PivotMember } from '../pivot/types'
import type { PivotFilterFormValues } from '../pivot/pivotFilterForm.utils'
import { DirectionEnum, PivotFieldTypeEnum, PivotFilterKindEnum } from '@ztechapps/common.contract'
import { describePivotCondition, describePivotValueComparison, describePivotValueFilter } from '@ztechapps/common.utils'
import { BusyButton, SaveCancelButtons } from '@ztechapps/webui.button'
import { FormModal } from '@ztechapps/webui.form'
import { useModalContext } from '@ztechapps/webui.modal'
import { Alert, ShowIf } from '@ztechapps/webui.util'
import { listPivotMembers } from '../pivot/listPivotMembers'
import {
  hasVisiblePivotFilterError,
  listPivotRankMeasures,
  toPivotFilterFormValues,
  toPivotFilterSetting,
  validatePivotFilterForm,
} from '../pivot/pivotFilterForm.utils'
import { findPivotFilterKind, findPivotLayoutItem } from '../pivot/pivotLayout.utils'
import { toPivotFieldLabel } from '../pivot/toPivotFieldLabel'
import { usePivotViewerContext } from '../PivotViewer/Context'
import { PivotFilterConditionPane } from './PivotFilterConditionPane'
import { PivotFilterSelectionPane } from './PivotFilterSelectionPane'
import { PivotFilterValuePane } from './PivotFilterValuePane'
import styles from './PivotFilterModal.module.scss'

/** What a custom trigger receives — the click that opens the dialog. */
export type PivotFilterModalTriggerProps = { onClick: () => void }

/** Props for the pivot filter dialog. */
export type PivotFilterModalProps = {
  data: PivotData
  field: PivotLayoutField
  layout: Pick<PivotLayout, 'rows' | 'columns' | 'measures' | 'filters'>
  timeZone: string
  /** Called with what the field filters by once Apply is clicked, or null once Clear filter is clicked. */
  onApply: (setting: PivotFilterSetting | null) => void
  sortDirection?: DirectionEnum
  onSort?: (direction?: DirectionEnum) => void
  trigger?: (props: PivotFilterModalTriggerProps) => ReactNode
}

const KIND_LABELS: Record<PivotFilterKindEnum, string> = {
  [PivotFilterKindEnum.Selection]: 'Selection',
  [PivotFilterKindEnum.Condition]: 'Condition',
  [PivotFilterKindEnum.Value]: 'Value',
}

type PivotFilterBodyProps = Omit<PivotFilterModalProps, 'onApply' | 'onSort' | 'trigger' | 'timeZone'> & {
  entry?: PivotLayoutFilter
  activeKind?: PivotFilterKindEnum
  fieldType?: PivotFieldTypeEnum
  members: PivotMember[]
  /** The kinds this field can be filtered by, Selection always first. */
  kinds: PivotFilterKindEnum[]
  hasSort: boolean
}

// the filter the field holds now, in a few words, for the notice a kind switch shows
const describeActive = ({
  entry,
  activeKind,
  fieldType,
  layout,
}: Pick<PivotFilterBodyProps, 'entry' | 'activeKind' | 'fieldType' | 'layout'>): string => {
  if (!entry) return ''
  if (activeKind === PivotFilterKindEnum.Condition && entry.conditionFilter)
    return describePivotCondition(entry.conditionFilter, fieldType ?? PivotFieldTypeEnum.String)
  if (activeKind === PivotFilterKindEnum.Value)
    return entry.valueComparison
      ? describePivotValueComparison(entry.valueComparison)
      : describePivotValueFilter(layout, entry)
  return entry.dateRange ? `${entry.dateRange.from} to ${entry.dateRange.to}` : 'some members left out'
}

const PivotFilterBody = (props: PivotFilterBodyProps) => {
  const { data, field, layout, entry, activeKind, fieldType, members, kinds, hasSort } = props
  const { values, setFieldValue } = useFormikContext<PivotFilterFormValues>()
  const isUngroupedDate = fieldType === PivotFieldTypeEnum.Date && field.dateGroup === undefined
  const activeText = describeActive({ entry, activeKind, fieldType, layout })

  return (
    <Stack gap={3}>
      <ShowIf condition={hasSort}>
        <div role="group" aria-label={`Sort ${field.field}`} className="btn-group btn-group-sm">
          <BusyButton
            variant="outline-secondary"
            icon="sort-alpha-asc"
            text="A to Z"
            active={values.sort === DirectionEnum.asc}
            onClick={() => setFieldValue('sort', values.sort === DirectionEnum.asc ? '' : DirectionEnum.asc)}
          />
          <BusyButton
            variant="outline-secondary"
            icon="sort-alpha-desc"
            text="Z to A"
            active={values.sort === DirectionEnum.desc}
            onClick={() => setFieldValue('sort', values.sort === DirectionEnum.desc ? '' : DirectionEnum.desc)}
          />
        </div>
      </ShowIf>
      {/* a field with only a selection has nothing to switch to, so it shows its bare pane */}
      <ShowIf condition={kinds.length > 1}>
        <div role="group" aria-label="Filter kind" className="btn-group">
          {map(kinds, (kind) => (
            <BusyButton
              key={kind}
              variant="outline-secondary"
              text={KIND_LABELS[kind]}
              active={values.kind === kind}
              // a switch never validates, so Apply never greys out for an error the new pane does not show
              onClick={() => setFieldValue('kind', kind, false)}
            >
              <ShowIf condition={activeKind === kind}>
                <span className="badge text-bg-success ms-2">Active</span>
              </ShowIf>
            </BusyButton>
          ))}
        </div>
      </ShowIf>
      <ShowIf condition={activeKind !== undefined && values.kind !== activeKind}>
        <Alert type="warning" className="small mb-0">
          This field has a {activeKind?.toLowerCase()} filter ({activeText}). Applying a {values.kind.toLowerCase()}{' '}
          filter replaces it.
        </Alert>
      </ShowIf>
      <ShowIf condition={values.kind === PivotFilterKindEnum.Selection}>
        <PivotFilterSelectionPane members={members} showRange={isUngroupedDate} />
      </ShowIf>
      <ShowIf condition={values.kind === PivotFilterKindEnum.Condition}>
        <PivotFilterConditionPane fieldType={fieldType ?? PivotFieldTypeEnum.String} />
      </ShowIf>
      <ShowIf condition={values.kind === PivotFilterKindEnum.Value}>
        <PivotFilterValuePane data={data} field={field} layout={layout} />
      </ShowIf>
    </Stack>
  )
}

type PivotFilterFooterProps = FormikProps<PivotFilterFormValues> & { onClear: () => void }

const PivotFilterFooter = ({ onClear, isSubmitting, values, errors, touched, submitCount }: PivotFilterFooterProps) => {
  const { toggleShowModal } = useModalContext()
  return (
    <div className="d-flex justify-content-between flex-fill">
      <BusyButton
        bsStyle="warning"
        text="Clear filter"
        onClick={() => {
          onClear()
          toggleShowModal()
        }}
      />
      <SaveCancelButtons
        saveText="Apply"
        saveIcon="filter"
        // No hover tooltip: the shared one opens beside the button and covers its sibling. The labels name the actions.
        saveTitle=""
        saveType="submit"
        cancelTitle=""
        cancelType="reset"
        // disabled exactly while an error shows, so a click never meets an error the form has not shown yet
        disabled={hasVisiblePivotFilterError({ kind: values.kind, errors, touched, submitCount })}
        isBusy={isSubmitting}
      />
    </div>
  )
}

// Mounted per open, so the member list and the staged values are built from the layout as it is then — a closed
// filter control costs nothing.
const PivotFilterDialog = ({
  data,
  field,
  layout,
  timeZone,
  onApply,
  sortDirection,
  onSort,
}: PivotFilterModalProps) => {
  const { container } = usePivotViewerContext()
  const entry = findPivotLayoutItem(layout.filters, field)
  const activeKind = findPivotFilterKind(entry)
  const pivotField = data.fields.find((item) => item.name === field.field)
  const fieldType = pivotField?.type
  const [members] = useState(() => listPivotMembers({ data, field, timeZone }))
  const [initialValues] = useState(() =>
    toPivotFilterFormValues({ entry, activeKind, members, fieldType, layout, sortDirection })
  )
  const onApplyRef = useRef(onApply)
  onApplyRef.current = onApply
  // a stable component, so a re-render of the dialog never remounts the footer under a click
  const footer = useCallback(
    (formik: FormikProps<PivotFilterFormValues>) => (
      <PivotFilterFooter {...formik} onClear={() => onApplyRef.current(null)} />
    ),
    []
  )

  const isRanked = !!findPivotLayoutItem(layout.rows, field) || !!findPivotLayoutItem(layout.columns, field)
  const canCondition =
    fieldType !== undefined && !(fieldType === PivotFieldTypeEnum.Date && field.dateGroup !== undefined)
  const canValue = isRanked && !isEmpty(listPivotRankMeasures(layout))
  const kinds = [
    PivotFilterKindEnum.Selection,
    ...(canCondition ? [PivotFilterKindEnum.Condition] : []),
    ...(canValue ? [PivotFilterKindEnum.Value] : []),
  ]

  return (
    <FormModal<PivotFilterFormValues>
      trigger={() => null}
      show
      title={`Filter: ${toPivotFieldLabel(pivotField, field.field, field.dateGroup)}`}
      size="md"
      container={container}
      initialValues={initialValues}
      validate={(values) => validatePivotFilterForm(values, fieldType ?? PivotFieldTypeEnum.String)}
      onSubmit={(values) => {
        onApply(toPivotFilterSetting({ values, layout, field, fieldType: fieldType ?? PivotFieldTypeEnum.String }))
        // a Rows member order replaces the row-by-value order, so an unchanged sort is never sent
        if (onSort && (values.sort || undefined) !== sortDirection) onSort(values.sort || undefined)
      }}
      footer={footer}
    >
      <PivotFilterBody
        data={data}
        field={field}
        layout={layout}
        entry={entry}
        activeKind={activeKind}
        fieldType={fieldType}
        members={members}
        kinds={kinds}
        hasSort={!!onSort}
      />
    </FormModal>
  )
}

/**
 * A filter control opening the one filter dialog of a pivot field: Selection, Condition or Value, a single kind at a
 * time, staged until Apply. The default control is a funnel, or a gear beside the funnel when the field also sorts.
 */
export const PivotFilterModal = (props: PivotFilterModalProps) => {
  const { field, layout, onSort, trigger } = props
  // a fresh count remounts the dialog, so each open starts from the layout as it is
  const [openCount, setOpenCount] = useState(0)
  const open = () => setOpenCount((count) => count + 1)
  const isActive = findPivotFilterKind(findPivotLayoutItem(layout.filters, field)) !== undefined

  return (
    <>
      {trigger ? (
        trigger({ onClick: open })
      ) : (
        <span>
          <ShowIf condition={!!onSort && isActive}>
            <BusyButton
              variant="link"
              bsSize="sm"
              className={`p-0 ${styles.filterButton}`}
              icon="filter"
              active
              aria-label={`Filter ${field.field}`}
              onClick={open}
            />
          </ShowIf>
          <BusyButton
            variant="link"
            bsSize="sm"
            className={`p-0 ${styles.filterButton}`}
            icon={onSort ? 'gear' : 'filter'}
            active={!onSort && isActive}
            aria-label={onSort ? `Open ${field.field} settings` : `Filter ${field.field}`}
            onClick={open}
          />
        </span>
      )}
      <ShowIf condition={openCount > 0}>
        <PivotFilterDialog key={openCount} {...props} />
      </ShowIf>
    </>
  )
}
